import { SourceLinks } from '@/components/ui/source-links';
import { Church, Swords } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { DiscoveryCard } from './discovery-card';

interface ReligiousLifeCardProps {
    religiousLife?: CultureByBook['religiousLife'];
    religiousEnvironment?: CultureByBook['religiousEnvironment'];
    isExpanded?: boolean;
    onToggleExpansion?: () => void;
    defaultExpanded?: boolean;
    title?: string;
    expandedItems: Set<string>;
    toggleItemExpansion: (id: string) => void;
}

export function ReligiousLifeCard({
    religiousLife,
    religiousEnvironment,
    isExpanded: controlledIsExpanded,
    onToggleExpansion: controlledOnToggleExpansion,
    defaultExpanded = false,
    title = "Religious Life & Institutions",
    expandedItems,
    toggleItemExpansion
}: ReligiousLifeCardProps) {
    const [internalIsExpanded, setInternalIsExpanded] = useState(defaultExpanded);

    const isExpanded = controlledIsExpanded !== undefined ? controlledIsExpanded : internalIsExpanded;
    const onToggleExpansion = controlledOnToggleExpansion !== undefined
        ? controlledOnToggleExpansion
        : () => setInternalIsExpanded(!internalIsExpanded);

    if (!religiousLife && !religiousEnvironment) return null;

    return (
        <DiscoveryCard
            isExpanded={isExpanded}
            onToggle={onToggleExpansion}
            title={title}
            icon={Church}
        >
            <div className="space-y-6 mt-6">
                {/* Religious Groups */}
                {religiousLife?.groups?.length > 0 && (
                    <div className="space-y-4">
                        <div className="text-[10px] font-bold text-foreground/50 uppercase tracking-[0.2em] border-b border-border/10 pb-2">Religious Groups</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {religiousLife.groups.map((group, index) => {
                                const itemId = `religious-group-${index}`;
                                const isItemExpanded = expandedItems.has(itemId);
                                return (
                                    <div key={index} className="space-y-1.5 border-l border-primary/10 pl-6 relative">
                                        <div className="flex items-center justify-between">
                                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">{group.name}</div>
                                            <button
                                                onClick={() => toggleItemExpansion(itemId)}
                                                className="text-[9px] font-bold text-primary/40 hover:text-primary transition-colors uppercase tracking-widest bg-primary/5 px-2 py-0.5 rounded"
                                            >
                                                {isItemExpanded ? 'Less' : 'More'}
                                            </button>
                                        </div>
                                        <p className="text-sm text-foreground/70 font-light leading-relaxed"><span className="font-medium text-foreground/40">Beliefs:</span> {group.beliefs}</p>
                                        {isItemExpanded && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                className="space-y-2 pt-2 border-t border-border/5 mt-2"
                                            >
                                                <p className="text-sm text-foreground/70 font-light leading-relaxed"><span className="font-medium text-foreground/40">Practices:</span> {group.practices}</p>
                                                <p className="text-sm text-foreground/70 font-light leading-relaxed"><span className="font-medium text-foreground/40">Social Role:</span> {group.socialRole}</p>
                                                {group.sources?.length > 0 && (
                                                    <SourceLinks sources={group.sources} className="mt-2" />
                                                )}
                                            </motion.div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Other Religious Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {religiousLife?.templeOperations && (
                        <div className="space-y-1.5 border-l border-primary/10 pl-6">
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">Temple/Tabernacle Operations</div>
                            <p className="text-sm text-foreground/70 font-light leading-relaxed">{religiousLife.templeOperations.description}</p>
                            {religiousLife.templeOperations.sources && (
                                <SourceLinks sources={religiousLife.templeOperations.sources} className="mt-2" />
                            )}
                        </div>
                    )}
                    {religiousLife?.synagoguePractices && (
                        <div className="space-y-1.5 border-l border-primary/10 pl-6">
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">Synagogue Practices</div>
                            <p className="text-sm text-foreground/70 font-light leading-relaxed">{religiousLife.synagoguePractices.description}</p>
                            {religiousLife.synagoguePractices.sources && (
                                <SourceLinks sources={religiousLife.synagoguePractices.sources} className="mt-2" />
                            )}
                        </div>
                    )}
                    {religiousLife?.theologicalDevelopments && (
                        <div className="space-y-1.5 border-l border-primary/10 pl-6 md:col-span-2">
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">Theological Developments</div>
                            <p className="text-sm text-foreground/70 font-light leading-relaxed">{religiousLife.theologicalDevelopments.description}</p>
                            {religiousLife.theologicalDevelopments.sources && (
                                <SourceLinks sources={religiousLife.theologicalDevelopments.sources} className="mt-2" />
                            )}
                        </div>
                    )}
                </div>

                {/* Competing Beliefs */}
                {religiousEnvironment?.competingBeliefs?.length > 0 && (
                    <div className="space-y-4">
                        <div className="text-[10px] font-bold text-foreground/50 uppercase tracking-[0.2em] border-b border-border/10 pb-2 flex items-center gap-2">
                            <Swords className="h-3 w-3" />
                            Competing/Surrounding Beliefs
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {religiousEnvironment.competingBeliefs.map((belief, index) => {
                                const itemId = `competing-belief-${index}`;
                                const isItemExpanded = expandedItems.has(itemId);
                                return (
                                    <div key={index} className="space-y-1.5 border-l border-primary/10 pl-6">
                                        <div className="flex items-center justify-between">
                                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">{belief.name}</div>
                                            <button
                                                onClick={() => toggleItemExpansion(itemId)}
                                                className="text-[9px] font-bold text-primary/40 hover:text-primary transition-colors uppercase tracking-widest bg-primary/5 px-2 py-0.5 rounded"
                                            >
                                                {isItemExpanded ? 'Less' : 'More'}
                                            </button>
                                        </div>
                                        <p className="text-sm text-foreground/70 font-light leading-relaxed">{belief.description}</p>
                                        {isItemExpanded && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                className="space-y-2 pt-2 border-t border-border/5 mt-2"
                                            >
                                                <p className="text-sm text-foreground/70 font-light leading-relaxed"><span className="font-medium text-foreground/40">Prevalence:</span> {belief.prevalence}</p>
                                                {belief.sources?.length > 0 && (
                                                    <SourceLinks sources={belief.sources} className="mt-2" />
                                                )}
                                            </motion.div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Syncretism Concerns */}
                {religiousEnvironment?.syncretismConcerns && (
                    <div className="space-y-2 border-l-2 border-orange-400/20 pl-6 py-1">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-500/60 flex items-center gap-1.5">
                            Syncretism Concerns
                        </div>
                        <p className="text-sm text-foreground/70 font-light leading-relaxed italic">{religiousEnvironment.syncretismConcerns.description}</p>
                        {religiousEnvironment.syncretismConcerns.sources && (
                            <SourceLinks sources={religiousEnvironment.syncretismConcerns.sources} className="mt-2" />
                        )}
                    </div>
                )}
            </div>
        </DiscoveryCard>
    );
}
